/**
 * Global game audio (Web Audio). One master gain drives the volume bar in the ESC settings.
 *  - play(name)        — a UI/global one-shot (portal, door, level-up, npc talk) at full volume.
 *  - playAt(name,x,z)  — a PROXIMITY one-shot (attack/block/chat): volume falls off with distance
 *                        from the local player (the "listener"); silent past `maxDist`.
 *  - startAmbient/stop — a looping bed (city ambient).
 * Buffers are decoded once. The AudioContext is resumed on the first user gesture (autoplay policy).
 */

export type SoundName =
  | "portal_open" | "portal_teleport" | "portal_loop"
  | "door" | "attack" | "block" | "levelup" | "npc_talk" | "chat" | "ambient_city"
  | "step1" | "step2" | "step3" | "smoke" | "your_turn";

const FILES: Record<SoundName, string> = {
  portal_open: "/assets/sound/portal_open.ogg",       // a portal swirls into being
  portal_teleport: "/assets/sound/portal_teleport.ogg", // stepping through / warping
  portal_loop: "/assets/sound/portal_loop.ogg",         // the hum while a portal stays open
  door: "/assets/sound/door.ogg",
  attack: "/assets/sound/attack.ogg",
  block: "/assets/sound/block.ogg",
  levelup: "/assets/sound/levelup.ogg",
  npc_talk: "/assets/sound/npc_talk.ogg",
  chat: "/assets/sound/chat.ogg",
  ambient_city: "/assets/sound/ambient_city.ogg",
  // Footsteps (grass) — alternated per step while walking/running; smoke = lighting a Blunt.
  step1: "/assets/sound/step1.ogg",
  step2: "/assets/sound/step2.ogg",
  step3: "/assets/sound/step3.ogg",
  smoke: "/assets/sound/smoke.ogg",
  your_turn: "/assets/sound/your_turn.ogg", // a soft confirm chime when it's your turn to act at poker
};

const STORAGE_KEY = "gl-volume";
const DEFAULT_VOLUME = 0.2;
/** How far (world units) a proximity sound carries before it's inaudible. */
const HEAR_DIST = 16;

class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<SoundName, AudioBuffer>();
  private volume = this.loadVolume();
  private lx = 0; private lz = 0;                 // listener (local player) world position
  private ambient: { src: AudioBufferSourceNode; gain: GainNode; name: SoundName } | null = null;
  /** Looping positional sounds keyed by id (e.g. one per open portal). Volume is driven per-frame. */
  private readonly loops = new Map<string, { src: AudioBufferSourceNode | null; gain: GainNode; name: SoundName }>();
  private started = false;

  private loadVolume(): number {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : DEFAULT_VOLUME;
  }

  /** Create the context + master gain, preload buffers, and arm a one-time gesture unlock. */
  init(): void {
    if (this.started) return;
    this.started = true;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
    for (const name of Object.keys(FILES) as SoundName[]) void this.load(name);
    const unlock = (): void => { void this.ctx?.resume(); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
  }

  private async load(name: SoundName): Promise<void> {
    if (!this.ctx) return;
    try {
      const buf = await (await fetch(FILES[name])).arrayBuffer();
      this.buffers.set(name, await this.ctx.decodeAudioData(buf));
      if (this.ambient && this.ambient.name === name && this.ambient.src.buffer == null) this.restartAmbient();
      for (const e of this.loops.values()) if (e.name === name && !e.src) this.armLoop(e); // start loops that were waiting on this buffer
    } catch { /* missing/failed sound — silently skip */ }
  }

  getVolume(): number { return this.volume; }
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    localStorage.setItem(STORAGE_KEY, String(this.volume));
    if (this.master) this.master.gain.value = this.volume;
  }

  /** Update the listener position (local player, world units) for proximity falloff. */
  setListener(wx: number, wz: number): void { this.lx = wx; this.lz = wz; }

  private one(name: SoundName, gain: number): void {
    const buf = this.buffers.get(name);
    if (!this.ctx || !this.master || !buf || gain <= 0.001) return;
    if (this.ctx.state === "suspended") void this.ctx.resume();
    const src = this.ctx.createBufferSource(); src.buffer = buf;
    const g = this.ctx.createGain(); g.gain.value = gain;
    src.connect(g); g.connect(this.master); src.start();
  }

  /** A global/UI one-shot at full (master) volume. */
  play(name: SoundName): void { this.one(name, 1); }

  /** A proximity one-shot — quieter the farther the source is from the local player; silent past
   *  HEAR_DIST. `vol` scales the base level (e.g. quieter footsteps). */
  playAt(name: SoundName, wx: number, wz: number, maxDist = HEAR_DIST, vol = 1): void {
    const d = Math.hypot(wx - this.lx, wz - this.lz);
    const g = Math.max(0, 1 - d / maxDist);
    this.one(name, g * g * vol); // square for a natural rolloff
  }

  /** Highest volume a positional loop reaches (right on top of it) — kept below ambient/one-shots. */
  private static readonly LOOP_MAX_GAIN = 0.6;

  /** Start a looping positional sound under `id` (idempotent). Volume starts silent — drive it with
   *  setLoopAt each frame. Used for "while open" beds like the portal wobble. */
  startLoop(id: string, name: SoundName): void {
    if (!this.ctx || !this.master || this.loops.has(id)) return;
    const gain = this.ctx.createGain(); gain.gain.value = 0; gain.connect(this.master);
    const entry = { src: null as AudioBufferSourceNode | null, gain, name };
    this.loops.set(id, entry);
    this.armLoop(entry); // no-op until the buffer is decoded; load() re-arms it
  }

  private armLoop(entry: { src: AudioBufferSourceNode | null; gain: GainNode; name: SoundName }): void {
    if (!this.ctx) return;
    const buf = this.buffers.get(entry.name);
    if (!buf) return;
    const src = this.ctx.createBufferSource(); src.buffer = buf; src.loop = true; src.connect(entry.gain);
    if (this.ctx.state === "suspended") void this.ctx.resume().then(() => { try { src.start(); } catch { /* already */ } });
    else src.start();
    entry.src = src;
  }

  /** Position a running loop relative to the listener — quieter the farther away; silent past maxDist. */
  setLoopAt(id: string, wx: number, wz: number, maxDist = HEAR_DIST): void {
    const e = this.loops.get(id);
    if (!e) return;
    const d = Math.hypot(wx - this.lx, wz - this.lz);
    const g = Math.max(0, 1 - d / maxDist);
    e.gain.gain.value = g * g * AudioManager.LOOP_MAX_GAIN;
  }

  /** Stop and discard the loop under `id` (no-op if absent). */
  stopLoop(id: string): void {
    const e = this.loops.get(id);
    if (!e) return;
    try { e.src?.stop(); } catch { /* not started */ }
    e.gain.disconnect();
    this.loops.delete(id);
  }

  private restartAmbient(): void {
    if (!this.ctx || !this.master || !this.ambient) return;
    const buf = this.buffers.get(this.ambient.name);
    if (!buf) return;
    const src = this.ctx.createBufferSource(); src.buffer = buf; src.loop = true;
    src.connect(this.ambient.gain); src.start();
    this.ambient.src = src;
  }

  /** Start (or keep) a looping ambient bed. No-op if it's already this one. */
  startAmbient(name: SoundName): void {
    if (this.ambient?.name === name) return;
    this.stopAmbient();
    if (!this.ctx || !this.master) return;
    const gain = this.ctx.createGain(); gain.gain.value = 0.45; gain.connect(this.master);
    const src = this.ctx.createBufferSource(); src.loop = true; src.connect(gain);
    this.ambient = { src, gain, name };
    const buf = this.buffers.get(name);
    if (buf) { src.buffer = buf; if (this.ctx.state !== "suspended") src.start(); else void this.ctx.resume().then(() => { try { src.start(); } catch { /* already */ } }); }
  }

  stopAmbient(): void {
    if (!this.ambient) return;
    try { this.ambient.src.stop(); } catch { /* not started */ }
    this.ambient = null;
  }
}

export const audio = new AudioManager();
